import React, { createContext, useContext, useState, useRef, useEffect } from "react";
import { io } from "socket.io-client";
import { ProductContext } from "./AppContext"; // Assuming AppContext exports ProductContext

const SocketContext = createContext(null); // Initialize with null for clarity

const normalizeNotificationPayload = (data) => {
  const moduleKey = data?.module_id ?? data?.moduleId ?? data?.module ?? data?.module_name;
  const normalizedModuleId =
    moduleKey === undefined || moduleKey === null || moduleKey === ""
      ? null
      : Number.isNaN(Number(moduleKey))
        ? moduleKey
        : Number(moduleKey);

  return {
    ...data,
    ...(normalizedModuleId !== null ? { module_id: normalizedModuleId } : {}),
    created_at: data?.created_at || new Date().toISOString(),
    received_at: new Date().toISOString(),
  };
};

const showBrowserNotification = (notification) => {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return;
  }

  const title =
    notification?.notification ||
    notification?.title ||
    "New Notification";

  const body =
    notification?.message ||
    notification?.body ||
    "You received a new notification.";

  const openNotification = () => {
    try {
      const browserNotification = new Notification(title, {
        body,
        tag: String(notification?.id ?? notification?._id ?? `${title}-${Date.now()}`),
        renotify: true,
      });

      browserNotification.onclick = () => {
        window.focus();
        browserNotification.close();
      };
    } catch (error) {
      console.error("Browser notification failed:", error);
    }
  };

  if (Notification.permission === "granted") {
    openNotification();
  }
};

const requestBrowserNotificationPermission = async () => {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return "unsupported";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error("Notification permission request failed:", error);
    return "error";
  }
};

function SocketValueContext({ children }) {
  const { userData } = useContext(ProductContext);
  const socketRef = useRef(null);
  const [socket, setSocket] = useState(null); // Use state to hold the socket instance

  const [notificationlist, setNotificationList] = useState([]);

  useEffect(() => {
    // Only connect if userData exists and a socket hasn't been initialized yet
    if (userData?.currentUser?.id && !socketRef.current) {
      console.log('Attempting to connect socket...');

      const newSocket = io('https://api.mirai.healthcare/', {
        reconnection: false,
        reconnectionAttempts: 0,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        // You might want to add auth headers here if needed for authentication
        // auth: {
        //   token: localStorage.getItem("token") // Example of sending token
        // }
      });

      socketRef.current = newSocket; // Store the socket instance in the ref
      setSocket(newSocket); // Update the state with the new socket

      const handleConnect = () => {
        console.log('Socket connected!');
        if (userData?.currentUser?.username) {
          newSocket.emit('setUserId',{userId:userData.currentUser.username,schema:userData.currentUser.schema}); // Use currentUser.username
        }
      };

      const handleNewNotification = (data) => {
        const normalizedNotification = normalizeNotificationPayload(data);
        setNotificationList((prevList) => [...prevList, normalizedNotification]);
        showBrowserNotification(normalizedNotification);
      };

      // Emit setUserId when the socket connects
      newSocket.on('connect', handleConnect);
      newSocket.on('newNotification', handleNewNotification);

      // Handle disconnection
      newSocket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
      });

      // Handle connection errors
      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      // Cleanup function to close the socket when the component unmounts
      return () => {
        console.log('Disconnecting socket...');
        newSocket.off('connect', handleConnect);
        newSocket.off('newNotification', handleNewNotification);
        newSocket.off('disconnect');
        newSocket.off('connect_error');
        newSocket.disconnect();
        socketRef.current = null;
        setSocket(null);
      };
    } else if (userData?.currentUser?.id && socketRef.current && !socket) {
      // This case handles potential re-renders where socketRef.current might be set
      // but the state `socket` is not. This ensures `socket` state is always in sync.
      setSocket(socketRef.current);
    }
  }, [userData?.currentUser?.id, userData?.currentUser?.username, userData?.currentUser?.schema]); // Re-run effect if userData changes

  // If userData changes and currentUser.username is present, and socket is already connected,
  // re-emit setUserId to ensure the correct ID is set for the currently logged-in user.
  useEffect(() => {
    if (socket && userData?.currentUser?.id) {
      console.log('Re-emitting setUserId:', userData.currentUser.username);
      socket.emit('setUserId', {userId:userData.currentUser.username,schema:userData.currentUser.schema});
    }
  }, [socket, userData?.currentUser?.id, userData?.currentUser?.username, userData?.currentUser?.schema]);


  return (
    <SocketContext.Provider
      value={{
        socket: socket,
        notificationlist,
        setNotificationList,
        requestBrowserNotificationPermission,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export { SocketContext, SocketValueContext };
